import { BulletBars, WaterfallBridge } from '../../charts'
import { CfoInteractiveChart } from '../../analysis'
import { formatCount, formatCurrencyExact, formatDays, formatShare } from '../ceoFormat'
import type { CeoCockpitResponse } from '../ceoCockpitTypes'
import { chartWidthFor, useContainerWidth } from '../useContainerWidth'

export function CeoCustomersPanel({ payload, onAsk }: { payload: CeoCockpitResponse; onAsk?: (prompt: string) => void }) {
  const [bridgeRef, bridgeHostWidth] = useContainerWidth<HTMLDivElement>()
  const customers = payload.customers
  if (!customers) return payload.current_source ? (
    <section className="ceo-section rise" data-testid="ceo-customers-unavailable">
      <p className="ceo-kicker">Customers</p>
      <h3>Customer detail is unavailable</h3>
      <p className="ceo-muted">Monthly native statements do not qualify customer concentration, new customers, or dated A/R risk.</p>
    </section>
  ) : null
  const bridgeWidth = chartWidthFor(bridgeHostWidth, 920, 560)
  const movement = customers.movement
  const top = Array.isArray(customers.top) ? customers.top.slice(0, 10) : []
  const atRisk = Array.isArray(customers.at_risk) ? customers.at_risk : []
  const concentration = customers.concentration
  const newLogos = customers.new_logos
  return (
    <section className="ceo-section rise" id="ceo-customers" data-testid="ceo-customers" data-fg-tour="ceo-customers">
      <p className="ceo-kicker">Customers</p>
      {movement ? (
        <CfoInteractiveChart hint="Ask AI to explain customer movement" onAnalyze={() => onAsk?.('Explain customer revenue movement this morning.')} ariaLabel="Explain customer movement">
          <div className="ceo-bridge" ref={bridgeRef}>
            <WaterfallBridge
              start={{ label: 'Start', value: movement.start }}
              steps={[
                { label: 'New', delta: movement.new },
                { label: 'Expansion', delta: movement.expansion },
                { label: 'Contraction', delta: movement.contraction },
                { label: 'Churn', delta: movement.churn },
                ...(movement.reactivation ? [{ label: 'Reactivation', delta: movement.reactivation }] : []),
              ]}
              end={{ label: 'End', value: movement.end }}
              width={bridgeWidth}
              ariaLabel="Customer revenue movement"
              formatValue={(value) => formatCurrencyExact(value)}
            />
          </div>
        </CfoInteractiveChart>
      ) : (
        <div className="ceo-not-tracked is-compact" data-testid="ceo-movement-not-prepared">
          <h3>Customer movement is not prepared</h3>
          <p>
            The start → new → expansion → contraction → churn bridge fills once a full trailing year of customer-level
            sales is on the books.
            {newLogos && newLogos.ytd > 0 ? ` New customers: ${formatCount(newLogos.mtd)} this month, ${formatCount(newLogos.ytd)} this year.` : ''}
          </p>
        </div>
      )}
      <div className="ceo-inv-cols">
        <div>
          <p className="ceo-kicker">Concentration · top {top.length || 10}</p>
          {top.length ? (
            <BulletBars
              ariaLabel="Top customers by year-to-date sales, with cumulative share"
              cumulativeShare
              items={top.map((row, index) => ({
                key: row.key,
                label: row.label,
                actual: row.ytd?.actual ?? null,
                share: row.share_ytd,
                rank: row.rank || index + 1,
                deltaRank: row.delta_rank,
              }))}
              formatValue={(value) => formatCurrencyExact(value)}
            />
          ) : (
            <p className="ceo-muted">Not prepared — no customer-level sales yet.</p>
          )}
          {concentration ? (
            <p className="ceo-muted">
              Top 1 {formatShare(concentration.top1_share)} · Top 5 {formatShare(concentration.top5_share)} · Top 10{' '}
              {formatShare(concentration.top10_share)} · HHI{' '}
              {concentration.hhi != null && Number.isFinite(concentration.hhi) ? concentration.hhi.toFixed(3) : 'Not prepared'}
            </p>
          ) : null}
        </div>
        <div>
          <p className="ceo-kicker">At-risk A/R</p>
          {atRisk.length ? (
            <ul className="ceo-plain-list">
              {atRisk.map((row) => (
                <li key={row.customer_id}>
                  <b>{row.customer}</b>
                  <span>{formatCurrencyExact(row.overdue_ar)} overdue</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ceo-muted">No customer is flagged at risk this morning.</p>
          )}
          {customers.dso != null || customers.ar_total != null ? (
            <p className="ceo-muted">
              {customers.ar_total != null ? `Open A/R ${formatCurrencyExact(customers.ar_total)}` : ''}
              {customers.ar_total != null && customers.dso != null ? ' · ' : ''}
              {customers.dso != null ? `DSO ${formatDays(customers.dso)}` : ''}
              {customers.dso != null && customers.dso_delta != null && Number.isFinite(customers.dso_delta)
                ? ` (${customers.dso_delta > 0 ? '+' : ''}${Math.round(customers.dso_delta)} vs prior)`
                : ''}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )
}
